import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { styled } from '@goodfynd/theme';
import { observer } from 'mobx-react-lite';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Container, Layout } from '../../../components';
import ToggleButtons from '../../../components/ToggleButtons';
import type { ToggleItem } from '../../../components/ToggleButtons/types';
import strings from '../../../config/strings';
import {
  SearchProvider,
  useSearch,
  useSearchDispatch,
} from '../../../context/search-context';
import SearchInputs from '../../../context/search-context/SearchInputs';
import { SearchResults } from '../../../context/search-context/SearchResults';
import type {
  SearchContextResults,
  SearchContextType,
} from '../../../context/search-context/types';
import { setPageTitle } from '../../../utils/base';
import type { NextPageWithLayout } from '../../_app';
const CONTEXT_DICTIONARY: Dictionary<SearchContextType, string> = {
  events: strings.labels.events,
  items: strings.labels.items,
  vendors: strings.labels.trucks,
};

const StyledContainer = styled(Container, {
  paddingBottom: 40,
  paddingTop: 68,
  '@sm': {
    paddingTop: 178,
  },
});

const StyledSearchControls = styled('section', {
  marginTop: 24,
  '@lg': {
    alignItems: 'center',
    display: 'flex',
    gap: 16,
    justifyContent: 'space-between',
  },
});

function isSearchContextType(value: unknown): value is SearchContextType {
  if (typeof value !== 'string') {
    return false;
  }

  return ['events', 'items', 'vendors'].includes(value);
}

function getSearchContextFromRouteParam(
  contextParam: string | string[] | undefined
): SearchContextType | undefined {
  const raw = Array.isArray(contextParam) ? contextParam[0] : contextParam;
  return isSearchContextType(raw) ? raw : undefined;
}

const Search: NextPageWithLayout = () => {
  const router = useRouter();
  const { phrase, eventSearch, productSearch, vendorSearch, context } =
    useSearch();
  const { refetch, refetchAll, setContext, updateTerm } = useSearchDispatch();

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const routeContext = getSearchContextFromRouteParam(
      router.query.context as string | string[] | undefined
    );
    if (routeContext && routeContext !== context) {
      setContext(routeContext);
    }
  }, [context, router.isReady, router.query.context, setContext]);

  const renderContextLabel = useCallback(
    (
      contextKey: SearchContextType,
      results?: SearchContextResults<unknown>
    ) => {
      return results?.isFetching && !results.total
        ? CONTEXT_DICTIONARY[contextKey]
        : `${CONTEXT_DICTIONARY[contextKey]} (${results?.total || 0})`;
    },
    []
  );

  const options = useMemo<ToggleItem<SearchContextType>[]>(
    () => [
      {
        label: renderContextLabel('vendors', vendorSearch),
        value: 'vendors',
      },
      {
        label: renderContextLabel('items', productSearch),
        value: 'items',
      },
      {
        label: renderContextLabel('events', eventSearch),
        value: 'events',
      },
    ],
    [eventSearch, productSearch, renderContextLabel, vendorSearch]
  );

  let defaultOption: ToggleItem<SearchContextType> = options[0];
  if (context === 'vendors') {
    defaultOption = {
      label: renderContextLabel('vendors', vendorSearch),
      value: 'vendors',
    };
  } else if (context === 'items') {
    defaultOption = {
      label: renderContextLabel('items', productSearch),
      value: 'items',
    };
  } else if (context === 'events') {
    defaultOption = {
      label: renderContextLabel('events', eventSearch),
      value: 'events',
    };
  }

  return (
    <>
      <Head>
        <title>{setPageTitle('Search')}</title>
        <meta content="noindex" name="robots" />
      </Head>

      <StyledContainer>
        <SearchInputs
          page="search"
          phrase={phrase}
          refetch={refetch}
          refetchAll={refetchAll}
          updatePhrase={updateTerm}
        />

        <StyledSearchControls>
          <ToggleButtons<SearchContextType>
            defaultOption={defaultOption}
            options={options}
            onChange={(newContext: SearchContextType) => {
              const nextQuery = { ...router.query };
              delete nextQuery.context;

              router.push(
                {
                  pathname: `/search/${newContext}`,
                  query: nextQuery as Record<string, string | string[]>,
                },
                undefined,
                { shallow: true }
              );
            }}
          />
        </StyledSearchControls>

        <SearchResults />
      </StyledContainer>
    </>
  );
};

Search.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout headerShadow={false}>
      <SearchProvider>{page}</SearchProvider>
    </Layout>
  );
};

export default observer(Search);
